import React, { useEffect, useState } from 'react';
import { Wifi, WifiOff, CloudOff, Check } from 'lucide-react';
import { getPendingCount, subscribe, flushQueue } from '../lib/pitOfflineQueue';

const useOnlineStatus = () => {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);
  return online;
};

const usePendingCount = () => {
  const [count, setCount] = useState(getPendingCount());
  useEffect(() => {
    const unsubscribe = subscribe(() => setCount(getPendingCount()));
    return unsubscribe;
  }, []);
  return count;
};

export default function OfflineBanner() {
  const online = useOnlineStatus();
  const pending = usePendingCount();
  const [justSynced, setJustSynced] = useState(false);

  useEffect(() => {
    if (online && pending > 0) {
      flushQueue().then(({ sent }) => {
        if (sent > 0) {
          setJustSynced(true);
          setTimeout(() => setJustSynced(false), 4000);
        }
      });
    }
  }, [online, pending]);

  if (!online) {
    return (
      <div
        data-testid="offline-banner"
        className="bg-amber-900/40 border-b border-amber-700/50 text-amber-200 text-xs px-3 py-2 flex items-center justify-center gap-2"
      >
        <WifiOff className="w-3.5 h-3.5" />
        <span>
          <strong>Sin conexión</strong> · seguí cargando datos, se sincronizan al volver el WiFi
          {pending > 0 && <span className="ml-2 text-amber-100">· {pending} cambio{pending === 1 ? '' : 's'} pendiente{pending === 1 ? '' : 's'}</span>}
        </span>
      </div>
    );
  }

  if (pending > 0) {
    return (
      <div
        data-testid="offline-banner-syncing"
        className="bg-blue-900/40 border-b border-blue-700/50 text-blue-200 text-xs px-3 py-2 flex items-center justify-center gap-2"
      >
        <CloudOff className="w-3.5 h-3.5 animate-pulse" />
        <span>Sincronizando {pending} cambio{pending === 1 ? '' : 's'}…</span>
      </div>
    );
  }

  if (justSynced) {
    return (
      <div className="bg-green-900/40 border-b border-green-700/50 text-green-200 text-xs px-3 py-2 flex items-center justify-center gap-2">
        <Check className="w-3.5 h-3.5" />
        <span>Sincronización completa</span>
      </div>
    );
  }

  return null;
}

export { useOnlineStatus, usePendingCount };
